/**
 * FXB Cart Drawer
 * Vanilla JS, no dependencies. Matches existing theme's data-* attribute pattern.
 *
 * Hooks into:
 *   - Any element with [data-cart-drawer-open] → opens drawer
 *   - Any element with [data-cart-drawer-close] → closes drawer
 *   - Any <form action="/cart/add"> on the page → intercepts add-to-cart, updates drawer, opens it
 *
 * Upsells:
 *   Each product can have a metafield "fxb.upsell_products" of type list.product_reference
 *   containing up to 4 recommended products. When a product is in the cart and has upsells
 *   configured, they're shown in the drawer's upsell block.
 *
 * Public API (exposed on window.CartDrawer):
 *   CartDrawer.open()
 *   CartDrawer.close()
 *   CartDrawer.refresh()  — re-fetch cart and re-render
 *   CartDrawer.addItem(variantId, qty, properties)  — programmatic add
 */

(function () {
  'use strict';

  const SELECTORS = {
    drawer: '[data-cart-drawer]',
    overlay: '.cart-drawer__overlay',
    panel: '.cart-drawer__panel',
    open: '[data-cart-drawer-open]',
    close: '[data-cart-drawer-close]',
    body: '[data-cart-drawer-body]',
    empty: '[data-cart-drawer-empty]',
    form: '[data-cart-drawer-form]',
    items: '[data-cart-drawer-items]',
    item: '[data-cart-drawer-item]',
    footer: '[data-cart-drawer-footer]',
    count: '[data-cart-drawer-count]',
    total: '[data-cart-drawer-total]',
    note: '[data-cart-drawer-note]',
    checkout: '[data-cart-drawer-checkout]',
    qtyInput: '[data-cart-drawer-qty-input]',
    qtyMinus: '[data-cart-drawer-qty-minus]',
    qtyPlus: '[data-cart-drawer-qty-plus]',
    remove: '[data-cart-drawer-remove]',
    upsells: '[data-cart-drawer-upsells]',
    upsellsGrid: '[data-cart-drawer-upsells-grid]',
    upsellTemplate: '[data-cart-drawer-upsell-template]',
    cartCount: '.cart-count' // header cart count badge
  };

  const ENDPOINTS = {
    add: '/cart/add.js',
    update: '/cart/update.js',
    change: '/cart/change.js',
    get: '/cart.js',
    products: '/products/'
  };

  // ========================================================
  // Main class
  // ========================================================
  class CartDrawer {
    constructor() {
      this.drawer = document.querySelector(SELECTORS.drawer);
      if (!this.drawer) return;

      this.panel = this.drawer.querySelector(SELECTORS.panel);
      this.body = this.drawer.querySelector(SELECTORS.body);
      this.emptyState = this.drawer.querySelector(SELECTORS.empty);
      this.form = this.drawer.querySelector(SELECTORS.form);
      this.itemsList = this.drawer.querySelector(SELECTORS.items);
      this.footer = this.drawer.querySelector(SELECTORS.footer);
      this.countEl = this.drawer.querySelector(SELECTORS.count);
      this.totalEl = this.drawer.querySelector(SELECTORS.total);
      this.noteInput = this.drawer.querySelector(SELECTORS.note);
      this.upsellsWrap = this.drawer.querySelector(SELECTORS.upsells);
      this.upsellsGrid = this.drawer.querySelector(SELECTORS.upsellsGrid);
      this.upsellTemplate = document.querySelector(SELECTORS.upsellTemplate);

      this.lastFocusedElement = null;
      this.noteDebounceTimer = null;
      this.upsellCache = new Map(); // productHandle → product JSON

      this._bindEvents();
      this._interceptAddToCartForms();
    }

    // ------------------------------------------------------
    // Public API
    // ------------------------------------------------------

    open() {
      if (!this.drawer) return;
      this.lastFocusedElement = document.activeElement;
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-drawer-open');
      // Focus the close button for keyboard users
      requestAnimationFrame(() => {
        const closeBtn = this.drawer.querySelector('.cart-drawer__close');
        if (closeBtn) closeBtn.focus();
      });
      this._trapFocus();
      // Refresh upsells based on current cart
      this._loadUpsells();
    }

    close() {
      if (!this.drawer) return;
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-drawer-open');
      this._releaseFocusTrap();
      if (this.lastFocusedElement && this.lastFocusedElement.focus) {
        this.lastFocusedElement.focus();
      }
    }

    isOpen() {
      return this.drawer && this.drawer.getAttribute('aria-hidden') === 'false';
    }

    async refresh() {
      const cart = await this._fetchCart();
      this._renderCart(cart);
      return cart;
    }

    async addItem(variantId, qty = 1, properties = {}) {
      const body = {
        items: [{ id: Number(variantId), quantity: qty, properties }]
      };
      const res = await fetch(ENDPOINTS.add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Could not add to cart');
      }
      await this.refresh();
      this.open();
      return res.json();
    }

    // ------------------------------------------------------
    // Event binding
    // ------------------------------------------------------

    _bindEvents() {
      // Open/close buttons (delegated — works for dynamic content)
      document.addEventListener('click', (e) => {
        const openBtn = e.target.closest(SELECTORS.open);
        if (openBtn) {
          e.preventDefault();
          this.open();
          return;
        }
        const closeBtn = e.target.closest(SELECTORS.close);
        if (closeBtn && this.drawer.contains(closeBtn)) {
          e.preventDefault();
          this.close();
          return;
        }
      });

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });

      // Delegated clicks inside the drawer
      this.drawer.addEventListener('click', (e) => {
        // Remove button
        const removeBtn = e.target.closest(SELECTORS.remove);
        if (removeBtn) {
          e.preventDefault();
          this._handleRemove(removeBtn);
          return;
        }
        // Qty minus
        const minusBtn = e.target.closest(SELECTORS.qtyMinus);
        if (minusBtn) {
          e.preventDefault();
          this._handleQtyStep(minusBtn, -1);
          return;
        }
        // Qty plus
        const plusBtn = e.target.closest(SELECTORS.qtyPlus);
        if (plusBtn) {
          e.preventDefault();
          this._handleQtyStep(plusBtn, 1);
          return;
        }
        // Upsell add
        const upsellAdd = e.target.closest('[data-upsell-add]');
        if (upsellAdd) {
          e.preventDefault();
          this._handleUpsellAdd(upsellAdd);
          return;
        }
      });

      // Qty input direct change
      this.drawer.addEventListener('change', (e) => {
        const qtyInput = e.target.closest(SELECTORS.qtyInput);
        if (qtyInput) {
          this._handleQtyChange(qtyInput);
        }
      });

      // Note input (debounced)
      if (this.noteInput) {
        this.noteInput.addEventListener('input', () => {
          clearTimeout(this.noteDebounceTimer);
          this.noteDebounceTimer = setTimeout(() => {
            this._updateCartNote(this.noteInput.value);
          }, 600);
        });
      }

      // Checkout button — submit form directly to /cart with name=checkout
      const checkoutBtn = this.drawer.querySelector(SELECTORS.checkout);
      if (checkoutBtn) {
        checkoutBtn.addEventListener('click', (e) => {
          e.preventDefault();
          // Post form to /cart with checkout submit, browser handles redirect
          const tempForm = document.createElement('form');
          tempForm.method = 'POST';
          tempForm.action = '/cart';
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'checkout';
          input.value = '';
          tempForm.appendChild(input);
          // Include current note if any
          if (this.noteInput && this.noteInput.value) {
            const noteInput = document.createElement('input');
            noteInput.type = 'hidden';
            noteInput.name = 'note';
            noteInput.value = this.noteInput.value;
            tempForm.appendChild(noteInput);
          }
          document.body.appendChild(tempForm);
          tempForm.submit();
        });
      }
    }

    // Intercept any add-to-cart form on the page
    _interceptAddToCartForms() {
      document.addEventListener('submit', async (e) => {
        const form = e.target;
        if (!form.matches('form[action*="/cart/add"]')) return;
        // Don't intercept forms that have opted out
        if (form.hasAttribute('data-no-drawer')) return;

        e.preventDefault();
        const submitBtn = form.querySelector('[type="submit"]');
        const originalBtnText = submitBtn ? submitBtn.textContent : null;
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.setAttribute('data-adding', '1');
        }

        try {
          const formData = new FormData(form);
          const res = await fetch(ENDPOINTS.add, {
            method: 'POST',
            body: formData,
            headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
          });

          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.description || err.message || 'Could not add to cart');
          }

          await this.refresh();
          this.open();
        } catch (err) {
          console.error('[CartDrawer] add-to-cart error:', err);
          // Fall back: let the form submit naturally
          form.removeEventListener('submit', arguments.callee);
          form.submit();
        } finally {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.removeAttribute('data-adding');
          }
        }
      }, true);
    }

    // ------------------------------------------------------
    // Cart operations
    // ------------------------------------------------------

    async _fetchCart() {
      const res = await fetch(ENDPOINTS.get, { headers: { 'Accept': 'application/json' } });
      return res.json();
    }

    async _changeCart(lineKey, qty) {
      const res = await fetch(ENDPOINTS.change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: qty })
      });
      if (!res.ok) throw new Error('Failed to update cart');
      return res.json();
    }

    async _updateCartNote(note) {
      try {
        await fetch(ENDPOINTS.update, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ note })
        });
      } catch (err) {
        console.error('[CartDrawer] note update failed:', err);
      }
    }

    async _handleQtyStep(btn, delta) {
      const lineKey = btn.dataset.lineKey;
      const itemEl = btn.closest(SELECTORS.item);
      const input = itemEl ? itemEl.querySelector(SELECTORS.qtyInput) : null;
      if (!input) return;
      const current = parseInt(input.value, 10) || 0;
      const next = Math.max(0, current + delta);
      await this._updateLine(itemEl, lineKey, next);
    }

    async _handleQtyChange(input) {
      const lineKey = input.dataset.lineKey;
      const itemEl = input.closest(SELECTORS.item);
      const qty = Math.max(0, parseInt(input.value, 10) || 0);
      await this._updateLine(itemEl, lineKey, qty);
    }

    async _handleRemove(btn) {
      const lineKey = btn.dataset.lineKey;
      const itemEl = btn.closest(SELECTORS.item);
      await this._updateLine(itemEl, lineKey, 0);
    }

    async _updateLine(itemEl, lineKey, qty) {
      if (itemEl) itemEl.classList.add('is-updating');
      try {
        const cart = await this._changeCart(lineKey, qty);
        this._renderCart(cart);
      } catch (err) {
        console.error('[CartDrawer] update line failed:', err);
        if (itemEl) itemEl.classList.remove('is-updating');
      }
    }

    // ------------------------------------------------------
    // Rendering
    // ------------------------------------------------------

    _renderCart(cart) {
      const count = cart.item_count || 0;

      // Update item count in header
      this._updateHeaderCount(count);

      // Update drawer count badge
      if (this.countEl) {
        this.countEl.textContent = count;
        this.countEl.setAttribute('data-count', count);
      }

      // Update total
      if (this.totalEl) {
        this.totalEl.textContent = this._formatMoney(cart.total_price);
      }

      // Toggle empty/filled state
      if (count === 0) {
        if (this.emptyState) {
          this.emptyState.hidden = false;
          this.emptyState.style.display = '';
        }
        if (this.form) {
          this.form.hidden = true;
          this.form.style.display = 'none';
        }
        if (this.footer) {
          this.footer.hidden = true;
          this.footer.style.display = 'none';
        }
      } else {
        if (this.emptyState) {
          this.emptyState.hidden = true;
          this.emptyState.style.display = 'none';
        }
        if (this.form) {
          this.form.hidden = false;
          this.form.style.display = '';
        }
        if (this.footer) {
          this.footer.hidden = false;
          this.footer.style.display = '';
        }
      }

      // Re-render line items
      this._renderItems(cart.items || []);

      // Update upsells (async — won't block main render)
      this._loadUpsells();

      // Announce to screen readers
      this._announce(count === 0 ? 'Cart is empty' : `Cart updated, ${count} item${count === 1 ? '' : 's'}`);
    }

    _renderItems(items) {
      if (!this.itemsList) return;
      // Simple approach: rebuild the list with minimal flicker
      const frag = document.createDocumentFragment();
      items.forEach(item => {
        frag.appendChild(this._buildItemElement(item));
      });
      this.itemsList.replaceChildren(frag);
    }

    _buildItemElement(item) {
      const li = document.createElement('li');
      li.className = 'cart-drawer__item';
      li.setAttribute('data-cart-drawer-item', '');
      li.setAttribute('data-variant-id', item.variant_id);
      li.setAttribute('data-line-key', item.key);

      const hasSale = item.original_line_price !== item.final_line_price;
      const variantLine = (item.variant_title && item.variant_title !== 'Default Title')
        ? `<div class="cart-drawer__item-variant">${this._escape(item.variant_title)}</div>` : '';
      const sellingPlanLine = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
        ? `<div class="cart-drawer__item-variant">${this._escape(item.selling_plan_allocation.selling_plan.name)}</div>` : '';

      const img = item.image
        ? `<img src="${this._escape(item.image)}" alt="${this._escape(item.product_title)}" loading="lazy" width="80" height="80">`
        : '';

      li.innerHTML = `
        <a href="${this._escape(item.url)}" class="cart-drawer__item-media">${img}</a>
        <div class="cart-drawer__item-body">
          <div class="cart-drawer__item-heading">
            <a href="${this._escape(item.url)}" class="cart-drawer__item-title">${this._escape(item.product_title)}</a>
            <button type="button" class="cart-drawer__item-remove" data-cart-drawer-remove data-line-key="${this._escape(item.key)}" aria-label="Remove ${this._escape(item.product_title)}">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          ${variantLine}
          ${sellingPlanLine}
          <div class="cart-drawer__item-footer">
            <div class="cart-drawer__qty" data-cart-drawer-qty>
              <button type="button" class="cart-drawer__qty-btn" data-cart-drawer-qty-minus data-line-key="${this._escape(item.key)}" aria-label="Decrease quantity">−</button>
              <input type="number" class="cart-drawer__qty-input" value="${item.quantity}" min="0" data-cart-drawer-qty-input data-line-key="${this._escape(item.key)}" aria-label="Quantity">
              <button type="button" class="cart-drawer__qty-btn" data-cart-drawer-qty-plus data-line-key="${this._escape(item.key)}" aria-label="Increase quantity">+</button>
            </div>
            <div class="cart-drawer__item-price">
              ${hasSale ? `<span class="cart-drawer__item-price-was">${this._formatMoney(item.original_line_price)}</span>` : ''}
              <span class="cart-drawer__item-price-now">${this._formatMoney(item.final_line_price)}</span>
            </div>
          </div>
        </div>
      `;
      return li;
    }

    _updateHeaderCount(count) {
      const headerCountEls = document.querySelectorAll(SELECTORS.cartCount);
      headerCountEls.forEach(el => {
        if (count > 0) {
          el.textContent = count;
          el.hidden = false;
          el.style.display = '';
        } else {
          el.hidden = true;
          el.style.display = 'none';
        }
      });

      // If header didn't have a .cart-count before (e.g. cart was empty on load), inject one
      if (count > 0 && headerCountEls.length === 0) {
        const cartLink = document.querySelector('.cart-link');
        if (cartLink) {
          const badge = document.createElement('span');
          badge.className = 'cart-count';
          badge.textContent = count;
          cartLink.appendChild(badge);
        }
      }
    }

    // ------------------------------------------------------
    // Upsells
    // ------------------------------------------------------

    async _loadUpsells() {
      if (!this.upsellsWrap || !this.upsellsGrid || !this.upsellTemplate) return;

      const cart = await this._fetchCart().catch(() => null);
      if (!cart || !cart.items || cart.items.length === 0) {
        this.upsellsWrap.hidden = true;
        this.upsellsGrid.innerHTML = '';
        return;
      }

      // Find bikes in cart — using "Bike" product type OR tag "bike"
      // (we check both to be flexible based on how products are tagged)
      const cartItemIds = new Set(cart.items.map(i => i.product_id));
      const bikeHandles = [];
      for (const item of cart.items) {
        const type = (item.product_type || '').toLowerCase();
        // We detect bikes by product_type = "Bike", "Dirt Bike", "Electric Bike", etc.
        // OR by tag "bike" — requires fetching product detail
        if (type.includes('bike')) {
          bikeHandles.push(item.handle);
        }
      }

      if (bikeHandles.length === 0) {
        this.upsellsWrap.hidden = true;
        this.upsellsGrid.innerHTML = '';
        return;
      }

      // For each bike, read its upsell metafield via Shopify's /products/handle.js endpoint
      // The metafield must be exposed to the Storefront API / included in product.metafields
      // Since product.js doesn't include metafields by default, we use a published JSON template:
      //   templates/product.upsells.json  (returns just the upsell list)
      // OR, more portable: fetch handle.js and read product.metafields.fxb.upsell_products
      //   which requires the metafield to be exposed to Storefront API.
      // For this implementation we use the section-rendering API:
      //   /products/{handle}?sections=cart-drawer-upsells  (requires matching section)
      // SIMPLEST path: use a JSON product template "product.upsells.json" that renders
      //   just the upsell product IDs as JSON. But that's extra setup.
      //
      // For first version: look for a DOM data-attribute on PDPs that declares upsells,
      // AND also look for upsells set via a global window.FXB_UPSELLS map keyed by handle.
      // This gives Toby two ways to configure without requiring metafield + Section Rendering setup.

      const collected = new Map(); // variantId → {product, variant}
      for (const handle of bikeHandles) {
        const upsells = await this._getUpsellsForHandle(handle);
        upsells.forEach(u => {
          if (!collected.has(u.variant.id) && !cartItemIds.has(u.product.id)) {
            collected.set(u.variant.id, u);
          }
        });
        if (collected.size >= 4) break;
      }

      if (collected.size === 0) {
        this.upsellsWrap.hidden = true;
        this.upsellsGrid.innerHTML = '';
        return;
      }

      // Render upsell cards (cap at 4)
      const cards = Array.from(collected.values()).slice(0, 4);
      const frag = document.createDocumentFragment();
      cards.forEach(u => frag.appendChild(this._buildUpsellCard(u.product, u.variant)));
      this.upsellsGrid.replaceChildren(frag);
      this.upsellsWrap.hidden = false;
    }

    async _getUpsellsForHandle(handle) {
      // Check window.FXB_UPSELLS first (set inline on product pages via Liquid)
      if (window.FXB_UPSELLS && window.FXB_UPSELLS[handle]) {
        return window.FXB_UPSELLS[handle];
      }

      // Fallback: fetch the product JSON (includes metafields if exposed to storefront)
      if (this.upsellCache.has(handle)) return this.upsellCache.get(handle);

      try {
        const res = await fetch(`${ENDPOINTS.products}${handle}.js`);
        if (!res.ok) return [];
        const product = await res.json();
        // Metafield access via /products/handle.js isn't standard — skip for now
        // Return empty; configuration happens via window.FXB_UPSELLS
        this.upsellCache.set(handle, []);
        return [];
      } catch (err) {
        return [];
      }
    }

    _buildUpsellCard(product, variant) {
      const node = this.upsellTemplate.content.cloneNode(true);
      const card = node.querySelector('[data-upsell-card]');
      const mediaLink = node.querySelector('[data-upsell-link]');
      const image = node.querySelector('[data-upsell-image]');
      const titleLink = node.querySelector('[data-upsell-title-link]');
      const priceEl = node.querySelector('[data-upsell-price]');
      const addBtn = node.querySelector('[data-upsell-add]');

      if (mediaLink) mediaLink.href = product.url || `/products/${product.handle}`;
      if (titleLink) {
        titleLink.href = product.url || `/products/${product.handle}`;
        titleLink.textContent = product.title;
      }
      if (image) {
        image.src = product.featured_image || product.image || '';
        image.alt = product.title;
      }
      if (priceEl) priceEl.textContent = this._formatMoney(variant.price);
      if (addBtn) addBtn.dataset.variantId = variant.id;

      return node;
    }

    async _handleUpsellAdd(btn) {
      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      btn.classList.add('is-loading');
      try {
        await this.addItem(variantId, 1);
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        btn.innerHTML = `
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          Added
        `;
        setTimeout(() => {
          btn.classList.remove('is-added');
          btn.innerHTML = `
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add
          `;
        }, 2000);
      } catch (err) {
        btn.classList.remove('is-loading');
        console.error('[CartDrawer] upsell add failed:', err);
        this._announce('Could not add to cart');
      }
    }

    // ------------------------------------------------------
    // Focus trap
    // ------------------------------------------------------

    _trapFocus() {
      if (this._focusTrapHandler) return;
      this._focusTrapHandler = (e) => {
        if (e.key !== 'Tab' || !this.isOpen()) return;
        const focusable = this._getFocusable();
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };
      document.addEventListener('keydown', this._focusTrapHandler);
    }

    _releaseFocusTrap() {
      if (this._focusTrapHandler) {
        document.removeEventListener('keydown', this._focusTrapHandler);
        this._focusTrapHandler = null;
      }
    }

    _getFocusable() {
      if (!this.panel) return [];
      return Array.from(this.panel.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      )).filter(el => !el.hidden && el.offsetParent !== null);
    }

    // ------------------------------------------------------
    // Utilities
    // ------------------------------------------------------

    _formatMoney(cents) {
      // Shopify-compatible money formatter (AUD)
      // Respects window.Shopify.currency or falls back to AUD
      const amount = (cents / 100).toFixed(2);
      const [whole, decimal] = amount.split('.');
      const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return `$${withCommas}.${decimal}`;
    }

    _escape(str) {
      if (str == null) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    _announce(msg) {
      let region = document.getElementById('cart-drawer-live');
      if (!region) {
        region = document.createElement('div');
        region.id = 'cart-drawer-live';
        region.className = 'cart-drawer__announce';
        region.setAttribute('aria-live', 'polite');
        region.setAttribute('role', 'status');
        document.body.appendChild(region);
      }
      region.textContent = '';
      setTimeout(() => { region.textContent = msg; }, 50);
    }
  }

  // ========================================================
  // Init
  // ========================================================
  function init() {
    if (window.CartDrawer && window.CartDrawer._instance) return;
    const instance = new CartDrawer();
    window.CartDrawer = {
      _instance: instance,
      open: () => instance.open(),
      close: () => instance.close(),
      refresh: () => instance.refresh(),
      addItem: (v, q, p) => instance.addItem(v, q, p),
      isOpen: () => instance.isOpen()
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();